import React, { useEffect, useState, useCallback, useReducer } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import Loading from "../components/loading";
import Dialogbox from "../components/dialogs/dialogBox";
import useAccessToken from "../auth/accessPermission";
import Breadcrumbs from "../components/breadcrumbs";
import useUserAccount from "../components/hooks/userAccountInfo";
import FormFields from "../components/secretForm/FormFields";
import SecretEditor from "../components/secretForm/SecretEditor";
import { useSecrets } from "../components/hooks/useSecrets";
import { createTags, validateJson } from "../components/utils/tagHelpers";
import { config } from "../components/config";

const containerStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  minHeight: "100vh",
};

// Reducer for form state management
const initialState = {
  secretName: "",
  jsonInput: "",
  envName: "",
  projName: "",
  secretType: "",
  selectedClusters: [],
  clusterMeta: [],
  selectedFile: null,
  fileContent: "",
  openCreate: false,
  openUpload: false,
  inputError: "",
};

function formReducer(state, action) {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'SET_MULTIPLE':
      return { ...state, ...action.values };
    case 'RESET_FORM':
      return initialState;
    default:
      return state;
  }
}

function CreateSecretForm() {
  const [state, dispatch] = useReducer(formReducer, initialState);
  const [errorMessage, setErrorMessage] = useState("");
  const [warningMessage, setWarningMessage] = useState("");
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openWarning, setOpenWarning] = useState(false);
  const [openBCWarning, setOpenBCWarning] = useState(false);
  const [openError, setOpenError] = useState(false);
  
  const navigate = useNavigate();
  const { instance } = useMsal();
  const { userName, nameParts } = useUserAccount(instance);
  const accessToken = useAccessToken([config.apiAccess]);
  
  const userDetails = instance.getActiveAccount().idTokenClaims.roles;
  const userRole = userDetails[0];
  const { loading, createSecret } = useSecrets();

  useEffect(() => {
    if (userRole && userRole !== config.roles[0] && userRole !== config.roles[2]) {
      dispatch({ type: 'SET_FIELD', field: 'projName', value: userRole });
    }
  }, [userRole]);

  useEffect(() => {
    if (state.fileContent) {
      dispatch({ type: 'SET_FIELD', field: 'jsonInput', value: state.fileContent });
    }
  }, [state.fileContent]);

  const handleSecretNameChange = useCallback((e) => {
    const value = e.target.value;
    const regex = /^[A-Za-z][A-Za-z0-9_-]*$/;

    if (regex.test(value) || value === "") {
      dispatch({ type: 'SET_MULTIPLE', values: { secretName: value, inputError: "" } });
    } else {
      dispatch({ type: 'SET_FIELD', field: 'inputError', value: "Must start with a letter, you can also add [0-9, -,_]" });
    }
  }, []);

  const handleClusterChange = useCallback((clusters) => {
    const meta = clusters.map((c) => {
      const [plat, vlt] = config.secretStore[c] || [];
      return { cluster: c, platformName: plat, vaultId: vlt };
    });
    dispatch({ type: 'SET_MULTIPLE', values: { selectedClusters: clusters, clusterMeta: meta } });
  }, []);

   const handleOpenWarning = () => {
    setOpenBCWarning(true);
    setWarningMessage("Changes not saved!");
  };


  const handleFileChange = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        dispatch({ type: 'SET_FIELD', field: 'fileContent', value: e.target.result });
      };
      reader.readAsText(file);
      dispatch({ type: 'SET_MULTIPLE', values: { selectedFile: file, openUpload: false } });
    }
  }, []);

  const handleCreateSecret = useCallback(async () => {
    const { secretName, jsonInput, envName, projName, secretType, clusterMeta } = state;

    if (!jsonInput || !secretName || !envName || !projName || !secretType) {
      setWarningMessage("All empty fields must be supplied");
      setOpenWarning(true);
      return;
    }

    if (!validateJson(jsonInput)) {
      setErrorMessage("Syntax error please check your input");
      setOpenError(true);
      return;
    }

    if (!clusterMeta.length) {
      setWarningMessage("Select at least one cluster");
      setOpenWarning(true);
      return;
    }

    const newJsonInput = secretType === "appsettings_file" 
      ? `{"appsettings.json": ${jsonInput}}`
      : jsonInput;

    const results = [];
    for (const { cluster, platformName, vaultId } of clusterMeta) {
      const tags = createTags(nameParts, envName, projName, platformName);
      const secretData = {
        secretName,
        secretValue: newJsonInput,
        tags,
      };

      const headersForCluster = {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
        ...(platformName !== config.platforms[0]
          ? { vaultName: vaultId }
          : { smRegion: vaultId }),
      };

      try {
        const resp = await createSecret(secretData, platformName, headersForCluster);
        results.push({ cluster, ok: resp.status === 200 });
      } catch (err) {
        const backendError = err.response?.data?.error || "Unknown error";
        results.push({ cluster, ok: false, error: backendError });
      }
    }

    const failures = results.filter((r) => !r.ok);
    if (!failures.length) {
      setOpenSuccess(true);
    } else {
      setErrorMessage(
        failures.map((f) => `${f.cluster}: ${f.error || "failed"}`).join("; ")
      );
      setOpenError(true);
    }
  }, [state, nameParts, accessToken, createSecret]);

  const handleSuccessAlertClose = useCallback(() => {
    setOpenSuccess(false);
    
    // Debug logging to see what we're working with
    console.log('ClusterMeta:', state.clusterMeta);
    console.log('ClusterMeta length:', state.clusterMeta.length);
    
    if (state.clusterMeta && state.clusterMeta.length === 1) {
      const clusterData = state.clusterMeta[0];
      console.log('Single cluster data:', clusterData);
      
      const { platformName, cluster, vaultId } = clusterData;
      
      // Check if all required values are present
      if (platformName && cluster && vaultId && state.secretName) {
        const navigationUrl = `/view-secret/${state.secretName}?platformName=${platformName}&selectedCluster=${cluster}&vaultName=${vaultId}`;
        console.log('Navigating to:', navigationUrl);
        navigate(navigationUrl);
      } else {
        console.warn('Missing required navigation parameters:', {
          platformName,
          cluster, 
          vaultId,
          secretName: state.secretName
        });
        // Fallback to the general secrets page
        navigate(`/secrets?name=${state.secretName}`);
      }
    } else {
      console.log('Multiple clusters or no clusters, navigating to secrets list');
      navigate(`/secrets?name=${state.secretName}`);
    }
  }, [state, navigate]);

  return (
    <div>
      <div style={{ paddingLeft: "2rem", marginBottom: "1rem" }}>
        <Breadcrumbs
          onIsModified={handleOpenWarning}
          isChange={state.selectedFile || state.openCreate}
        />
      </div>
      
      <div style={containerStyle}>
        {loading && <Loading open={loading} />}
        
        <Typography variant="h3" gutterBottom style={{ margin: 30, color: "#1976D2" }}>
          New secret
        </Typography>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <FormFields
            secretName={state.secretName}
            onSecretNameChange={handleSecretNameChange}
            inputError={state.inputError}
            secretType={state.secretType}
            onSecretTypeChange={(e) => dispatch({ type: 'SET_FIELD', field: 'secretType', value: e.target.value })}
            envName={state.envName}
            onEnvChange={(e) => dispatch({ type: 'SET_FIELD', field: 'envName', value: e.target.value })}
            projName={state.projName}
            onProjChange={(e) => dispatch({ type: 'SET_FIELD', field: 'projName', value: e.target.value })}
            userRole={userRole}
            onClusterChange={handleClusterChange}
          />

          <SecretEditor
            openCreate={state.openCreate}
            openUpload={state.openUpload}
            selectedFile={state.selectedFile}
            jsonInput={state.jsonInput}
            fileContent={state.fileContent}
            onCreateClick={() => dispatch({ type: 'SET_FIELD', field: 'openCreate', value: true })}
            onUploadClick={() => dispatch({ type: 'SET_FIELD', field: 'openUpload', value: true })}
            onFileChange={handleFileChange}
            onCodeChange={(value) => dispatch({ type: 'SET_FIELD', field: 'jsonInput', value })}
            onCreateSecret={handleCreateSecret}
            onCancel={() => {
              setOpenWarning(true);
              setWarningMessage("Changes not saved!");
            }}
          />

          {/* Dialog components */}
          <Box sx={{ width: "100%" }}>
            <Dialogbox
              open={openSuccess}
              onClose={handleSuccessAlertClose}
              title="Success"
              severity="success"
              message={
                <div>
                  {state.secretName} created successfully! <br />
                  Click on the close button to view secret
                </div>
              }
            />

            <Dialogbox
              open={openWarning}
              onClose={() => setOpenWarning(false)}
              title="Warning"
              severity="warning"
              message={
                <div>
                  {warningMessage} <br />
                  Click on the button to continue editing or discard
                </div>
              }
              onConfirm={() => navigate(0)}
              confirmText="Discard"
              closeText="Back to editing"
            />

            <Dialogbox
              open={openBCWarning}
              onClose={() => setOpenBCWarning(false)}
              title="Warning"
              severity="warning"
              message={
                <div>
                  {warningMessage} <br />
                  Click on the button to continue editing or discard
                </div>
              }
              onConfirm={() => navigate("/")}
              confirmText="Discard"
              closeText="Back to editing"
            />

            <Dialogbox
              open={openError}
              onClose={() => setOpenError(false)}
              title="Error"
              severity="error"
              message={
                <div>
                  Failed to create! <br />
                  Message: {errorMessage}
                </div>
              }
            />
          </Box>
        </div>
      </div>
    </div>
  );
}

export default CreateSecretForm;